import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Passwordless public CSAT page (`/csat/:token`) — NO login required.
 *
 * The post-resolution feedback email links here so casual customers (no portal
 * account) can rate the service in one tap. The token is the auth. Six states:
 * loading · active · submitting · submitted-just-now · already-submitted ·
 * expired · error. Mobile-first (single column, large tap targets).
 */
import { useEffect, useState } from 'react';
import { t } from '@/lib/i18n';
import { PublicLanguageToggle, usePublicLanguage } from '@/components/PublicLanguageToggle';
import { useParams } from 'react-router-dom';
import { Turnstile, captchaEnabled } from '@/components/Turnstile';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Field, Textarea } from '@/components/ui/Input';
import { RatingScale } from '@/components/ui/RatingScale';
import { ApiError, publicGet, publicPost } from '@/lib/api';
function Shell({ children }) {
    const lang = usePublicLanguage();
    return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-slate-50 p-4 dark:bg-slate-950", children: _jsxs("div", { className: "w-full max-w-sm space-y-2", children: [_jsx("div", { className: "flex justify-end", children: _jsx(PublicLanguageToggle, {}) }), _jsx(Card, { className: "w-full", children: _jsx(CardBody, { children: children }) })] }, lang) }));
}
function fmtDate(iso) {
    if (!iso)
        return '';
    try {
        return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(iso));
    }
    catch {
        return '';
    }
}
export function CsatPublicPage() {
    const { token } = useParams();
    const [load, setLoad] = useState('loading');
    const [survey, setSurvey] = useState(null);
    const [overall, setOverall] = useState(null);
    const [responses, setResponses] = useState({});
    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [justSubmitted, setJustSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [startedAt] = useState(() => Date.now());
    useEffect(() => {
        if (!token)
            return;
        publicGet(`/csat/${token}`)
            .then((s) => {
            setSurvey(s);
            setLoad(s.already_submitted ? 'already' : 'active');
        })
            .catch((e) => {
            // 410 = expired link → its own state; anything else → generic error.
            setLoad(e instanceof ApiError && e.status === 410 ? 'expired' : 'error');
        });
    }, [token]);
    const submit = async () => {
        if (!token || overall === null)
            return;
        setSubmitting(true);
        setError(null);
        try {
            await publicPost(`/csat/${token}`, {
                overall_rating: overall,
                responses: { ...responses, comment },
                time_on_page: Math.round((Date.now() - startedAt) / 1000),
                ...(captchaToken ? { captcha_token: captchaToken } : {}),
            });
            setJustSubmitted(true);
        }
        catch (e) {
            // A 410 here means someone already submitted (double-click / re-open).
            if (e instanceof ApiError && e.status === 410) {
                setLoad('already');
                if (survey)
                    setSurvey({ ...survey, already_submitted: true, overall_rating: overall });
            }
            else {
                setError(e instanceof Error ? e.message : 'Submission failed. Please try again.');
            }
        }
        finally {
            setSubmitting(false);
        }
    };
    const firstName = survey?.requester_first_name?.trim();
    const ref = survey?.ticket_number ? `Ref: ${survey.ticket_number}` : null;
    // --- Loading ---
    if (load === 'loading') {
        return _jsx(Shell, { children: _jsx("div", { className: "py-6 text-center text-sm text-slate-500", children: "Loading your feedback form\u2026" }) });
    }
    // --- Error ---
    if (load === 'error') {
        return (_jsxs(Shell, { children: [_jsx("div", { className: "text-lg font-semibold text-slate-800 dark:text-slate-100", children: "Something went wrong" }), _jsx("p", { className: "mt-2 text-sm text-slate-500", children: "We couldn't load your feedback form. Please refresh, or try again in a few minutes." })] }));
    }
    // --- Expired ---
    if (load === 'expired') {
        return (_jsxs(Shell, { children: [_jsx("div", { className: "text-lg font-semibold text-slate-800 dark:text-slate-100", children: "This link has expired" }), _jsx("p", { className: "mt-2 text-sm text-slate-500", children: "This feedback link has expired. If you'd still like to share your feedback, please contact your service desk team." })] }));
    }
    // --- Submitted just now ---
    if (justSubmitted) {
        return (_jsx(Shell, { children: _jsxs("div", { className: "flex flex-col items-center py-2 text-center", children: [_jsx("div", { className: "mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-3xl dark:bg-emerald-950/50", children: "\u2713" }), _jsxs("div", { className: "text-xl font-semibold text-slate-800 dark:text-slate-100", children: [t('Thanks'), firstName ? `, ${firstName}` : '', "!"] }), _jsx("p", { className: "mt-2 text-sm text-slate-500", children: t("We've recorded your {{n}}★ rating and shared it with the team. You can close this page.", { n: overall }) })] }) }));
    }
    // --- Already submitted (re-clicked) ---
    if (load === 'already') {
        return (_jsxs(Shell, { children: [_jsx("div", { className: "text-lg font-semibold text-slate-800 dark:text-slate-100", children: t("You've already shared your feedback") }), _jsxs("p", { className: "mt-2 text-sm text-slate-500", children: [survey?.overall_rating ? t('You rated us {{n}}★', { n: survey.overall_rating }) : t('Your feedback was recorded'), survey?.submitted_at ? ` on ${fmtDate(survey.submitted_at)}` : '', ". ", t('Thank you!')] })] }));
    }
    // --- Active form ---
    const questions = survey?.questions.questions ?? [];
    const overallQ = questions.find((q) => q.key === 'overall');
    const otherQs = questions.filter((q) => q.key !== 'overall');
    return (_jsx("div", { className: "flex min-h-screen items-start justify-center bg-slate-50 p-4 dark:bg-slate-950 sm:items-center", children: _jsx(Card, { className: "w-full max-w-sm", children: _jsxs(CardBody, { children: [ref && _jsx("div", { className: "mb-1 text-xs font-medium uppercase tracking-wide text-slate-400", children: ref }), _jsx("h1", { className: "text-xl font-semibold text-slate-900 dark:text-slate-100", children: firstName ? t('How was your service, {{name}}?', { name: firstName }) : t('How was your service?') }), _jsx("p", { className: "mt-1 text-sm text-slate-500", children: t('Your feedback helps us improve.') }), overallQ && (_jsxs("div", { className: "mt-6", children: [_jsx("div", { className: "text-sm font-medium", children: overallQ.label }), _jsx("div", { className: "mt-3", children: _jsx(RatingScale, { value: overall, onChange: setOverall, max: overallQ.scale ?? 5, ariaLabel: overallQ.label }) })] })), otherQs.map((q) => (_jsxs("div", { className: "mt-5", children: [_jsx("div", { className: "text-sm font-medium", children: q.label }), _jsx("div", { className: "mt-2", children: _jsx(RatingScale, { value: responses[q.key] ?? null, onChange: (n) => setResponses((p) => ({ ...p, [q.key]: n })), max: q.scale ?? 5, ariaLabel: q.label }) })] }, q.key))), _jsx("div", { className: "mt-5", children: _jsx(Field, { label: t('Anything else? (optional)'), children: _jsx(Textarea, { value: comment, onChange: (e) => setComment(e.target.value), placeholder: t('Share your thoughts'), maxLength: 2000 }) }) }), _jsx(Turnstile, { onToken: setCaptchaToken }), error && (_jsx("div", { className: "mt-3 rounded border border-rose-300 bg-rose-50 p-2 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300", children: error })), _jsx(Button, { className: "mt-5 h-12 w-full", onClick: submit, disabled: overall === null || submitting || (captchaEnabled && !captchaToken), children: submitting ? 'Submitting…' : 'Submit feedback' })] }) }) }));
}
